import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/apiClient";
import { devLog } from "@/lib/devLog";
import { Bell, CheckCircle } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

/**
 * Header bell — polls unread notifications every 30 s and opens a dropdown
 * on click with the most recent items. Marks items read on click and
 * navigates to the item's `link`.
 */
export function NotificationBell() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 15 } });
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (e) {
      // Bell polls in background — avoid toast spam on transient network hiccups,
      // but log for debuggability.
      devLog.error("[NotificationBell] load failed:", e);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  // Click-outside to close
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const clickItem = async (n) => {
    if (!n.read_at) {
      try {
        await api.post(`/notifications/${n.id}/read`);
      } catch (e) {
        devLog.error("[NotificationBell] mark-read failed:", e);
      }
    }
    setOpen(false);
    if (n.link) nav(n.link);
    load();
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
    } catch (e) {
      devLog.error("[NotificationBell] mark-all-read failed:", e);
    }
    load();
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="topbar-notifications"
        className="relative p-1.5 text-slate-400 hover:text-white hover:bg-white/[0.06] rounded-md transition-colors"
        title="Notifications"
      >
        <Bell size={14} />
        {unread > 0 && (
          <span
            data-testid="notification-unread-count"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 text-[9px] font-semibold text-white bg-[#EA580C] rounded-full flex items-center justify-center ring-2 ring-[#0B1220]"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 mt-2 w-96 bg-white text-slate-900 border border-slate-200 rounded-lg shadow-tile-hover z-50 overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-slate-500">Notifications</div>
            {unread > 0 && (
              <button
                onClick={markAll}
                data-testid="mark-all-read"
                className="text-[10.5px] text-[#EA580C] hover:text-[#c2410c] font-semibold uppercase tracking-[0.16em]"
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-400">
                <CheckCircle size={30} className="mx-auto mb-2.5 text-emerald-500" />
                All caught up
              </div>
            ) : items.map((n) => (
              <button
                key={n.id}
                onClick={() => clickItem(n)}
                data-testid={`notification-item-${n.id}`}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors ${n.read_at ? "opacity-60" : ""}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-slate-800 truncate">{n.title}</div>
                    {n.body && <div className="text-[11.5px] text-slate-500 mt-0.5 line-clamp-2">{n.body}</div>}
                    <div className="text-[9.5px] uppercase tracking-[0.16em] text-slate-400 mt-1.5">
                      {n.type} · {new Date(n.created_at).toLocaleString()}
                    </div>
                  </div>
                  {!n.read_at && <span className="w-2 h-2 rounded-full bg-[#EA580C] mt-1.5 flex-shrink-0" />}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
